import { z } from 'zod'
import { ExecutedOperationSchema, ExecutionOutcome, type ExecutedOperation } from './execute-result-data.ts'
import { quoteUntrusted } from '../lib/untrusted-text.ts'
import { UPLOAD_BATCH_MAX_ITEMS } from '../lib/batch-uploader.ts'

export const ChronologyNetworkSchema = z.object({
  id: z.string(), requestTime: z.number(), attemptSeq: z.number().optional(),
  method: z.string(), url: z.string(), status: z.number().nullable(),
  response: z.string().optional(), error: z.string().optional(), mirror: z.string().optional(),
})

export type ChronologyNetwork = z.infer<typeof ChronologyNetworkSchema>

export const JourneyChronologyEntrySchema = z.object({
  seq: z.number().int().nonnegative(),
  timestamp: z.string().datetime(),
  kind: z.enum(['step', 'attempt', 'result', 'observation', 'history', 'decision']),
  step: z.number().int().positive().optional(),
  recovery: z.boolean().optional(),
  substep: z.boolean().optional(),
  description: z.string(),
  result: z.string().optional(),
  outcome: ExecutionOutcome.optional(),
  url: z.string().optional(),
  context: z.string().optional(),
  layout: z.string().optional(),
  structure: z.string().optional(),
  actionIds: z.array(z.string().min(1)),
  operations: z.array(ExecutedOperationSchema).optional(),
})
export type JourneyChronologyEntry = z.infer<typeof JourneyChronologyEntrySchema>
/** One persisted write: entries in `seq` order. */
export const JourneyChronologyBatchSchema = z.array(JourneyChronologyEntrySchema).min(1).max(UPLOAD_BATCH_MAX_ITEMS)
export type JourneyChronologyInput = Omit<JourneyChronologyEntry, 'seq' | 'timestamp'>
export const JourneyChronologyResponseSchema = z.object({
  entries: z.array(JourneyChronologyEntrySchema),
})

/** Mid-ellipsis excerpt, so a long locator or target markup stays bounded in the prompt. */
export const boundedExcerpt = (value: string, limit: number): string => value.length <= limit ? value
  : `${value.slice(0, limit / 2)}\n… excerpt omitted …\n${value.slice(-limit / 2)}`

/** `<action> → <result>`, then what the operation acted on when recorded: a locator alone may not identify its element. */
export function renderExecutedOperation(operation: Pick<ExecutedOperation, 'action' | 'result' | 'target'>, shortenAction: (action: string) => string): string {
  return `${shortenAction(operation.action)} → ${operation.result}` + (operation.target ? `\n  acted on: ${boundedExcerpt(operation.target, 300)}` : '')
}

export interface JourneyChronologyRenderOptions {
  maxEntries?: number
  includeLayout?: boolean
  network?: readonly ChronologyNetwork[]
}

/** Keep the validator's captured statuses aligned with exactly the exchanges shown in the prompt. */
export function renderJourneyChronologySnapshot(entries: readonly JourneyChronologyEntry[], options: JourneyChronologyRenderOptions = {}): { text: string; network: ChronologyNetwork[] } {
  const shownNetwork: ChronologyNetwork[] = []
  const rows: JourneyChronologyEntry[] = []
  const network = new Map<string, ChronologyNetwork>()
  const attemptResults = new Map<number, number>()
  for (const entry of entries) {
    if (entry.kind === 'decision' || entry.kind === 'step') continue
    if (entry.kind === 'observation') {
      const previous = rows.at(-1)
      if (previous && previous.step === entry.step && previous.context === entry.context) {
        previous.structure = entry.structure
        previous.layout = entry.layout ?? previous.layout
      }
      continue
    }
    if (entry.kind === 'result') {
      const attempt = [...rows].reverse().find(row => row.kind === 'attempt' && row.step === entry.step
        && row.context === entry.context && row.recovery === entry.recovery && row.description === entry.description)
      if (attempt) {
        attemptResults.set(attempt.seq, entry.seq)
        rows.splice(rows.indexOf(attempt), 1)
      }
    }
    if (entry.kind === 'history' && entry.substep) {
      const execution = [...rows].reverse().find(row => row.kind === 'result'
        && row.step === entry.step && row.context === entry.context
        && row.actionIds.some(id => entry.actionIds.includes(id)))
      if (execution) {
        execution.result = entry.result ?? execution.result
        execution.structure = entry.structure ?? execution.structure
        execution.layout = entry.layout ?? execution.layout
        continue
      }
    }
    rows.push({ ...entry })
  }
  for (const exchange of options.network ?? []) network.set(exchange.id, exchange)
  if (!rows.length && !network.size) return { text: '(no actions performed yet)', network: [] }
  const networkFor = (seq: number | undefined): string => {
    const exchanges = [...network.values()].filter(n => (n.attemptSeq === undefined ? undefined : attemptResults.get(n.attemptSeq) ?? n.attemptSeq) === seq)
    if (!exchanges.length) return ''
    // Preserve failures when background polling would otherwise push them out of the excerpt.
    const failures = exchanges.filter(n => n.error || (n.status != null && n.status >= 400)).slice(-12)
    const remaining = 12 - failures.length
    const selected = new Set([...failures, ...(remaining ? exchanges.filter(n => !failures.includes(n)).slice(-remaining) : [])])
    const shown = exchanges.filter(n => selected.has(n)).slice(-12)
    shownNetwork.push(...shown.map(n => ({ ...n })))
    return '\n' + quoteUntrusted({ source: 'network-exchanges', text: [
      `Captured requests ${seq === undefined ? 'during setup' : 'during this action window'} (${exchanges.length}; timestamps are request start times):`,
      ...(exchanges.length > shown.length ? [`… ${exchanges.length - shown.length} other exchanges omitted`] : []),
      ...shown.map(n => `${new Date(n.requestTime).toISOString()} ${n.method} ${n.url} → ${n.status == null ? 'response unavailable' : `HTTP ${n.status}`}${n.mirror ? ` [clone response: ${n.mirror}]` : ''}`
        + (n.error ? `\n  Network error: ${n.error}` : '') + (n.response ? `\n  Response excerpt: ${n.response}` : '')),
    ].join('\n') })
  }

  // History is a navigation aid to recorded step snapshots, not another full page observation.
  const excerpt = (value: string, limit: number, step?: number): string => value.length <= limit ? value
    : `${value.slice(0, limit / 2)}\n… history excerpt omitted; ${step ? `read_page(${step})` : 'read the recorded snapshot'} for full page evidence …\n${value.slice(-limit / 2)}`
  const pageChange = (value: string, step?: number) => excerpt(value, 2000, step)
  const rendered = rows.slice(-(options.maxEntries ?? 24)).map(entry => {
    const networkText = networkFor(entry.seq)
    const label = entry.step ? `Step ${entry.step}` : 'Setup'
    const result = entry.kind === 'attempt' ? 'started; no result recorded yet' : entry.result
    // Inbox bodies and verification URLs remain available through the inbox capability, not repeated
    // in every subsequent prompt. Preserve the link reference and execution outcome instead.
    const summary = entry.description === 'Read email inbox' && result?.startsWith('Email inbox:')
      ? (result.includes('Email 1:') ? 'email received (links available from inbox)' : 'no matching email received')
      : result?.replace(/https?:\/\/[^\s"<>]+/g, '[URL omitted]')
    const page = entry.structure?.split('\n').filter(line => line.startsWith('· ')).slice(0, 12).join(' ')
      || (entry.structure && /LoaderCircle|AnimateSpin/.test(entry.structure) ? 'loading indicator present' : '')
    if (entry.operations !== undefined) {
      return `[${label}${entry.recovery ? ' recovery' : ''}${entry.context ? `; ${entry.context}` : ''}] Requested step action: ${entry.description}\n`
        + (entry.operations.length ? entry.operations.map(operation => renderExecutedOperation(operation, action => excerpt(action, 500, entry.step))
          + (operation.page_diff ? `\nObserved page change after this operation:\n${pageChange(operation.page_diff, entry.step)}` : '')).join('\n')
          + (summary ? `\nAttempt result: ${summary}` : '')
          + (entry.operations.every(operation => operation.page_diff === undefined) && entry.layout ? `\nObserved page change after this action:\n${pageChange(entry.layout, entry.step)}` : '')
          : `No browser operations executed. ${summary ?? ''}`) + networkText
    }
    return `[${label}${entry.recovery ? ' recovery' : ''}${entry.context ? `; ${entry.context}` : ''}] `
      + entry.description.replace(/https?:\/\/[^\s"<>]+/g, '[URL omitted]')
      + (summary ? ` → ${summary.slice(0, 500)}` : '')
      + (page ? ` | Observed: ${page.replace(/https?:\/\/[^\s"<>]+/g, '[URL omitted]').slice(0, 300)}` : '')
      + (options.includeLayout && entry.layout ? `\n${pageChange(entry.layout.replace(/https?:\/\/[^\s"<>]+/g, '[URL omitted]'), entry.step)}` : '')
      + networkText
  }).join('\n')
  const text = networkFor(undefined) + (rendered ? '\n' + rendered : '')
  return { text, network: shownNetwork }
}
